import { TAmbassadorOrLeader } from "@/app/api/listing/[slug]/listing.dto";
import ProfilePicture from "@/components/common/profile-picture";
import { formatImageUrl } from "@/lib/utils";
import { ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

type TFamilyCardProps = {
	icon: string;
	name: string;
	description: string;
	slug: string;
	numberOfProducts?: number;
	leaders: TAmbassadorOrLeader[];
	ambassadors: TAmbassadorOrLeader[];
	productLogos: Array<{ id: string; icon: string }>;
};

const FamilyCard = ({
	icon,
	name,
	description,
	slug,
	numberOfProducts = 10,
	leaders,
	ambassadors,
	productLogos,
}: TFamilyCardProps) => {
	return (
		<Link
			href={`/listing/${slug}`}
			className="group block w-full rounded bg-white px-4 py-4 shadow md:py-8 md:pl-4 md:pr-8"
		>
			<div className="flex flex-wrap items-center justify-between md:flex-nowrap">
				<div className="flex flex-wrap items-center gap-4 md:flex-nowrap">
					<div className="h-full w-20 p-2 md:w-fit">
						<Image
							src={icon || ""}
							alt={name}
							className="h-[170px] w-[170px] rounded object-contain p-1 text-brand-primary"
							width={80}
							height={80}
						/>
					</div>

					<div>
						<span className="text-lg font-semibold capitalize md:text-xl">{name}</span>
						<p className="line-clamp-3 max-w-2xl py-1 text-xs text-gray-600 md:text-sm mb-5">
							{description}
						</p>

						<span className="text-sm font-semibold uppercase tracking-widest">Leaders</span>
						{leaders && leaders.length > 0 ? (
							<div className="mt-2 flex items-center -space-x-4">
								{leaders.map(({ user }) => (
									<Link key={user?.id} href={`/${user?.UserProfile?.username}`}>
										<ProfilePicture
											profilePictureUrl={formatImageUrl(user?.image)}
											isVerified={user?.UserProfile?.isVerified}
											isFounder={user?.UserProfile?.isFounder}
											isAmbassador={false}
											className={{
												container: "h-16 w-16 rounded-full p-1",
												image: "h-14 w-14 rounded-full",
												badgeImage: "-bottom-6 h-12 w-12",
											}}
										/>
									</Link>
								))}
							</div>
						) : (
							<div className="mt-2 flex items-center -space-x-4">
								<Image
									src="/images/person-1.jpg"
									alt="person image"
									width={100}
									height={100}
									className="h-10 w-10 rounded-full border-2 border-white object-cover md:h-12 md:w-12"
								/>
								<Image
									src="/images/person-2.jpg"
									alt="person image"
									width={100}
									height={100}
									className="h-10 w-10 rounded-full border-2 border-white object-cover md:h-12 md:w-12"
								/>
								<Image
									src="/images/person-3.jpg"
									alt="person image"
									width={100}
									height={100}
									className="h-10 w-10 rounded-full border-2 border-white object-cover md:h-12 md:w-12"
								/>
							</div>
						)}
					</div>
				</div>

				{/* TODO: Need to make it dynamic */}
				<div className="mt-4 md:mt-0">
					<div className="flex items-center gap-1 text-gray-700">
						<span className="text-sm font-semibold uppercase tracking-widest">Ambassadors</span>
						<ArrowUpRightIcon className="h-5 w-5" />
					</div>
					{ambassadors && ambassadors.length > 0 ? (
						<div className="mt-2 flex items-center justify-center -space-x-4">
							{ambassadors.map(({ user }) => (
								<div key={user.id} className="flex gap-4">
									<Link href={`/${user.UserProfile?.username}`}>
										<ProfilePicture
											profilePictureUrl={formatImageUrl(user.image)}
											alternateText={user.name}
											isAmbassador={true}
											isVerified={user.UserProfile?.isVerified}
											isFounder={user.UserProfile?.isFounder}
											className={{
												container: "h-12 w-12 rounded-full lg:h-16 lg:w-16",
												image: "h-10 w-10 rounded-full lg:h-14 lg:w-14",
												badgeImage: "w-12",
											}}
										/>
									</Link>
								</div>
							))}
						</div>
					) : (
						<div className="mt-2 flex items-center -space-x-4">
							<Image
								src="/images/person-1.jpg"
								alt="person image"
								width={100}
								height={100}
								className="h-10 w-10 rounded-full border-2 border-white object-cover md:h-12 md:w-12"
							/>
						</div>
					)}
				</div>
			</div>

			<div className="mt-7 flex w-full flex-wrap items-center justify-end gap-4 md:flex-nowrap md:pl-4">
				<div className="flex w-full items-center justify-between gap-4 md:w-fit md:justify-center">
					<div className="flex -space-x-3">
						{productLogos.slice(0, 3).map(({ id, icon }) => (
							<Image
								key={id}
								src={icon}
								alt="Product logo"
								className="h-10 w-10 rounded-full border-2 border-white object-cover"
								width={40}
								height={40}
							/>
						))}
					</div>

					<div className="flex items-center justify-center gap-4">
						<span className="text-sm text-gray-600">
							{numberOfProducts}+ <span className="text-gray-800">Products</span>
						</span>
						<span>
							<ArrowRightIcon className="h-8 w-8 rounded-full p-1 text-brand-primary transition-all duration-300 group-hover:bg-brand-primary group-hover:text-white" />
						</span>
					</div>
				</div>
			</div>
		</Link>
	);
};

export default FamilyCard;
